import { useEffect } from 'react';
import {
  CapacityManagementRootResponse,
  TransactionsParams,
  TransactionsResponse,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';
import urlTemplate from 'url-template';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: TransactionsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: TransactionsParams;
};

export function useMyTransactions({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const myTransactions = useAxios(
    (axios, signal, { rootData, params }: RequestOptions) => {
      const link =
        rootData._links?.myTransactionsV2 ?? rootData._links?.myTransactions;
      if (!link) throw new Forbidden();

      const url = urlTemplate.parse(link.href).expand({});

      return axios.request<TransactionsResponse>({
        signal,
        url,
        params,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        // as this is a non-lazy request we want to handle the error only,
        // if we have at least one successful response
        if (isConstraintViolation(error) && myTransactions.response) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    myTransactions.execute(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return myTransactions;
}

export type MyTransactionsRequest = ReturnType<typeof useMyTransactions>;
